/**
 * Helpers for the Inventory Receiving new-product dialog. Product and
 * manufacturer suggestions come from the JDTech catalog; a typed name that
 * is not in the catalog is still kept.
 */

export function namesMatch(a, b) {
  return (
    String(a ?? "").trim().toLowerCase() ===
    String(b ?? "").trim().toLowerCase()
  );
}

/** `primary` wins when a name is in both lists. Sorted A–Z, ignoring case. */
export function mergeByName(primary, extra, nameKey) {
  const seen = new Set();
  const out = [];
  for (const row of [...(primary || []), ...(extra || [])]) {
    const name = String(row?.[nameKey] ?? "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...row, [nameKey]: name });
  }
  out.sort((a, b) =>
    a[nameKey].localeCompare(b[nameKey], undefined, { sensitivity: "base" })
  );
  return out;
}

/** True when `name` is set and the organization has no manufacturer by that name. */
export function isNewManufacturer(name, orgManufacturers) {
  const text = String(name ?? "").trim();
  if (!text) return false;
  return !(orgManufacturers || []).some((row) =>
    namesMatch(row.manufacturer_name, text)
  );
}

/**
 * Rows for the new-product dialog's Manufacturer picker (JD-464): the
 * organization's manufacturers, JD Tech catalog suggestions, names already
 * waiting on scanned lines and the current selection, merged by name. A
 * name the organization does not have yet is `pending` (shown "(new)") and
 * carries no id; the API creates it when receiving is completed.
 */
export function manufacturerPickerRows({
  orgManufacturers,
  suggestions,
  pendingNames,
  selected,
}) {
  const extra = [
    ...(suggestions || []),
    ...(pendingNames || []).map((name) => ({ manufacturer_name: name })),
    ...(selected ? [selected] : []),
  ];
  return mergeByName(orgManufacturers, extra, "manufacturer_name").map((row) =>
    isNewManufacturer(row.manufacturer_name, orgManufacturers)
      ? { ...row, manufacturer_id: null, pending: true }
      : row
  );
}

/**
 * Fields from a catalog product the user actually selected. Brand and
 * category stay only when the confirmed manufacturer is that product's
 * manufacturer (or the catalog product has none).
 */
export function catalogDetailForRow(selection, productName, manufacturerName) {
  const empty = {
    brand_name: null,
    category_name: null,
    product_type_id: null,
    product_type_name: null,
    catalog_product_id: null,
  };
  if (!selection || !namesMatch(selection.product_name, productName)) {
    return empty;
  }
  const keepParents =
    !selection.manufacturer_name ||
    namesMatch(selection.manufacturer_name, manufacturerName);
  return {
    brand_name: keepParents ? selection.brand_name || null : null,
    category_name: keepParents ? selection.category_name || null : null,
    product_type_id: selection.product_type_id ?? null,
    product_type_name: selection.product_type_name || null,
    catalog_product_id: selection.product_id ?? null,
  };
}
